export default class OverscrollIndicator extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-overscroll-indicator") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  constructor() {
    super();
    this.cleanup = new Set();
    this.isActive = false;
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup;

  /**
   * @type {boolean}
   */
  isActive;

  /**
   * Set up the component once it has been added to the DOM.
   */
  connectedCallback() {
    if (!this.isConnected) {
      return;
    }

    this.setup();

    /** @type {ReturnType<typeof requestAnimationFrame>} */
    let rafId;

    const update = () => {
      if (this.isConnected) {
        const start = Math.min(64, Math.floor(this.scrollTop));
        const end = Math.min(
          64,
          Math.floor(this.scrollHeight - this.clientHeight - this.scrollTop),
        );
        this.style.setProperty(
          "--shadow-start",
          `clamp(0rem, 0.25 * ${start}px, 1rem)`,
        );
        this.style.setProperty(
          "--shadow-end",
          `clamp(-1rem, -0.5 * ${end}px, 0rem)`,
        );
      }
      if (this.isActive) {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(update);
      }
    };

    /** @type {ReturnType<typeof setTimeout>} */
    let timeout;

    const start = () => {
      this.isActive = true;
      update();
      clearTimeout(timeout);
      timeout = setTimeout(() => (this.isActive = false));
    };

    this.addEventListener("scroll", start);
    this.cleanup.add(() => this.removeEventListener("scroll", start));

    const mutationObserver = new MutationObserver(() => update());
    mutationObserver.observe(this, { childList: true, subtree: true });
    this.cleanup.add(() => mutationObserver.disconnect());

    const resizeObserver = new ResizeObserver(() => update());
    resizeObserver.observe(this);
    this.cleanup.add(() => resizeObserver.disconnect());
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  setup() {
    Object.assign(this.style, {
      boxShadow: `
        inset 0 var(--shadow-start, 0px) 1rem -1.5rem #000,
        inset 0 var(--shadow-end, 0px) 1rem -1.5rem #000`,
      display: "block",
      overflow: "auto",
      position: "relative",
    });
  }
}
